import { IconGrid } from "@/assets/icons/geist/IconGrid";
import { IconCode } from "@/assets/icons/geist/IconCode";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useListEnvironmentInventory } from "@/queries/environment-queries";
import { useListProjects, useListProjectStates } from "@/queries/project-queries";
import { useListRunnerEnvironmentClasses } from "@/queries/runner-configuration-queries";
import { pluralizeWithCount } from "@/utils/strings";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export type RunnerUsageButtonsProps = {
    /** Configuration for local runner usage */
    localRunner?: boolean;
    /** Specific runner ID for cloud runners */
    runnerId?: string;
    /** Custom className for the container */
    className?: string;
    /** Test ID prefix for buttons */
    testIdPrefix?: string;
};

export const RunnerUsageButtons: FC<RunnerUsageButtonsProps> = ({
    localRunner = false,
    runnerId,
    className,
    testIdPrefix = "runner-usage",
}) => {
    const navigate = useNavigate();

    // Query for projects
    const { data: projectsData, isPending: isPendingProjects } = useListProjects();
    const { data: projectsStates, isLoading: isLoadingProjectsStates } = useListProjectStates(
        projectsData?.projects.map((p) => p.id),
    );

    // Query for environments
    const environmentQuery = localRunner ? { runnerKind: RunnerKind.LOCAL } : { runnerID: runnerId };

    const {
        data: environmentsData,
        hasNextPage: hasNextPageEnvironments,
        isPending: isPendingEnvironments,
    } = useListEnvironmentInventory(environmentQuery);

    // Query for environment classes (only needed for specific runners)
    const { data: environmentClasses } = useListRunnerEnvironmentClasses(localRunner ? "" : runnerId ?? "");

    // Calculate project count
    const numProjects = useMemo(() => {
        if (!projectsStates) return 0;

        if (localRunner) {
            return projectsStates.filter((p) => {
                return p.environmentClasses?.some(
                    (ec) => ec.environmentClass.case === "localRunner" && ec.environmentClass.value === true,
                );
            }).length;
        } else if (runnerId && environmentClasses) {
            const environmentClassIds = environmentClasses.map((ec) => ec.id);
            return projectsStates.filter((p) => {
                return p.environmentClasses?.some(
                    (ec) =>
                        ec.environmentClass.case === "environmentClassId" &&
                        environmentClassIds.includes(ec.environmentClass.value),
                );
            }).length;
        }

        return 0;
    }, [projectsStates, localRunner, runnerId, environmentClasses]);

    // Calculate environment count
    const numEnvironments = useMemo(() => {
        return environmentsData?.pages.flatMap((page) => page.environments).length ?? 0;
    }, [environmentsData]);

    // Generate navigation URLs
    const projectsUrl = localRunner ? "/projects?environmentClass=local" : `/projects?runnerId=${runnerId}`;

    const environmentsUrl = localRunner
        ? "/settings/environments?runner=local"
        : `/settings/environments?runner=${runnerId}`;

    return (
        <div className={className}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <Text className="text-sm font-bold text-content-secondary">Used by</Text>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <Button
                        LeadingIcon={IconGrid}
                        size="sm"
                        variant="secondary"
                        disabled={numProjects === 0}
                        loading={isPendingProjects || isLoadingProjectsStates}
                        onClick={() => navigate(projectsUrl)}
                        className="w-full sm:w-auto"
                        data-testid={`${testIdPrefix}-projects-button`}
                        data-tracking-id="view-runner-projects-runner-usage-buttons"
                    >
                        {pluralizeWithCount(numProjects, false, "Project")}
                    </Button>
                    <Button
                        LeadingIcon={IconCode}
                        size="sm"
                        variant="secondary"
                        disabled={numEnvironments === 0}
                        loading={isPendingEnvironments}
                        onClick={() => navigate(environmentsUrl)}
                        className="w-full sm:w-auto"
                        data-testid={`${testIdPrefix}-environments-button`}
                        data-tracking-id="view-runner-environments-runner-usage-buttons"
                    >
                        {pluralizeWithCount(numEnvironments, hasNextPageEnvironments, "Environment")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
